// One QueryClient for the app. Mutation errors become toasts (unless a mutation handles a status itself, like the
// 409 conflict dialog); query errors show inline, and only background refetch failures toast.
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { errorMessage, isApiError } from './api'

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: {
      /** true: never toast; number[]: do not toast these HTTP statuses (the caller shows its own UI). */
      silent?: boolean | number[]
    }
  }
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (err, query) => {
        if (isApiError(err) && err.status === 401) return
        if (query.state.data !== undefined) toast.error(errorMessage(err))
      },
    }),
    mutationCache: new MutationCache({
      onError: (err, _vars, _ctx, mutation) => {
        if (isApiError(err) && err.status === 401) return
        const silent = mutation.meta?.silent
        if (silent === true) return
        if (Array.isArray(silent) && isApiError(err) && silent.includes(err.status)) return
        toast.error(errorMessage(err))
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: (count, err) => {
          if (isApiError(err) && err.status >= 400 && err.status < 500) return false
          return count < 2
        },
        refetchOnWindowFocus: true,
      },
      mutations: { retry: false },
    },
  })
}
